import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import {
  collection,
  doc,
  getDocs,
  orderBy,
  query,
  serverTimestamp,
  updateDoc,
  where,
} from "firebase/firestore";
import { db } from "../../firebase/config";
import { useCmsAuth } from "../../context/CmsAuthContext";
import { Field, inputClass } from "../cmsApi.jsx";
import { LEAD_STATUS } from "../../lib/crmSchema";
import { ModuleHelpShell } from "../../components/help/ModuleHelpShell";
import { ModuleHelpToggle } from "../../components/help/ModuleHelpToggle";

export default function CmsLeadsEditor() {
  const { profile, user, isVendedor } = useCmsAuth();
  const [params] = useSearchParams();
  const clienteFilter = params.get("cliente") || "";
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [typeFilter, setTypeFilter] = useState("all");
  const [selected, setSelected] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      let q;
      if (profile?.role === "vendedor" && user?.uid) {
        q = query(
          collection(db, "leads"),
          where("assignedVendedorId", "==", user.uid),
          orderBy("createdAt", "desc")
        );
      } else if (clienteFilter) {
        q = query(
          collection(db, "leads"),
          where("clienteId", "==", clienteFilter),
          orderBy("createdAt", "desc")
        );
      } else {
        q = query(collection(db, "leads"), orderBy("createdAt", "desc"));
      }
      const snap = await getDocs(q);
      setItems(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
    } catch (err) {
      setError(
        err?.message ||
          "No se pudieron cargar leads. Si es índice faltante, despliegue firestore.indexes.json."
      );
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [profile?.role, user?.uid, clienteFilter]);

  useEffect(() => {
    load();
  }, [load]);

  const filtered = useMemo(() => {
    if (typeFilter === "all") return items;
    return items.filter((i) => i.type === typeFilter);
  }, [items, typeFilter]);

  const onStatus = async (leadId, status) => {
    setSaving(true);
    try {
      await updateDoc(doc(db, "leads", leadId), {
        status,
        updatedAt: serverTimestamp(),
      });
      await load();
      if (selected?.id === leadId) {
        setSelected((s) => (s ? { ...s, status } : s));
      }
    } catch (err) {
      setError(err?.message || "No se pudo actualizar.");
    } finally {
      setSaving(false);
    }
  };

  const fmtDate = (ts) => {
    if (!ts) return "—";
    const d = ts.toDate ? ts.toDate() : new Date(ts);
    return d.toLocaleString("es-SV");
  };

  return (
    <ModuleHelpShell moduleId="leads">
    <div className="space-y-8 max-w-5xl">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
        <h2 className="font-display text-3xl font-black">
          {isVendedor ? "Mis consultas y cotizaciones" : "Consultas y cotizaciones"}
        </h2>
        <p className="text-sm text-brand-dark/55 mt-1">
          Leads del sitio web asignados automáticamente o por lista de clientes.{" "}
          <Link to="/admin/clientes" className="text-brand-primary font-bold underline">
            Ir a clientes
          </Link>
        </p>
        </div>
        <ModuleHelpToggle />
      </div>

      <div className="flex flex-wrap gap-2">
        {[
          ["all", "Todas"],
          ["consulta", "Consultas"],
          ["cotizacion", "Cotizaciones"],
        ].map(([id, label]) => (
          <button
            key={id}
            type="button"
            onClick={() => setTypeFilter(id)}
            className={`px-3 py-1.5 rounded-full text-[10px] font-bold uppercase tracking-widest ${
              typeFilter === id ? "bg-brand-dark text-white" : "bg-white border border-brand-soft"
            }`}
          >
            {label}
          </button>
        ))}
        <button type="button" onClick={load} className="text-xs font-bold text-brand-primary px-2">
          Actualizar
        </button>
      </div>

      {clienteFilter && (
        <p className="text-xs text-brand-dark/50">
          Filtrado por cliente <code>{clienteFilter}</code>.{" "}
          <Link to="/admin/leads" className="underline text-brand-primary">
            Quitar filtro
          </Link>
        </p>
      )}

      {error && <p className="text-sm text-red-600">{error}</p>}
      {loading && <p className="text-sm text-brand-dark/50">Cargando…</p>}

      <div className="grid lg:grid-cols-5 gap-6">
        <ul className="lg:col-span-2 space-y-2">
          {!loading && filtered.length === 0 && (
            <li className="text-sm text-brand-dark/50">No hay leads.</li>
          )}
          {filtered.map((row) => (
            <li key={row.id}>
              <button
                type="button"
                onClick={() => setSelected(row)}
                className={`w-full text-left rounded-2xl border px-4 py-3 ${
                  selected?.id === row.id
                    ? "border-brand-primary bg-brand-primary/5"
                    : "border-brand-soft bg-white"
                }`}
              >
                <p className="text-[10px] font-bold uppercase tracking-widest text-brand-primary">
                  {row.type === "cotizacion" ? "Cotización" : "Consulta"} · {row.status || "nuevo"}
                </p>
                <p className="font-bold text-sm mt-1">{row.empresa || row.contactoNombre || row.email}</p>
                <p className="text-xs text-brand-dark/50">{fmtDate(row.createdAt)}</p>
              </button>
            </li>
          ))}
        </ul>

        <div className="lg:col-span-3 bg-white rounded-[28px] border border-brand-soft p-6 min-h-[280px]">
          {!selected && (
            <p className="text-sm text-brand-dark/50">Seleccione un lead para ver el detalle.</p>
          )}
          {selected && (
            <div className="space-y-4">
              <div>
                <h3 className="font-bold text-xl">
                  {selected.type === "cotizacion" ? "Cotización" : "Consulta"}
                </h3>
                <p className="text-xs text-brand-dark/50">{fmtDate(selected.createdAt)}</p>
              </div>
              <Field label="Estado">
                <select
                  className={inputClass}
                  disabled={saving}
                  value={selected.status || "nuevo"}
                  onChange={(e) => onStatus(selected.id, e.target.value)}
                >
                  {Object.entries(LEAD_STATUS).map(([k, label]) => (
                    <option key={k} value={k}>
                      {label}
                    </option>
                  ))}
                </select>
              </Field>
              <dl className="grid sm:grid-cols-2 gap-3 text-sm">
                <div>
                  <dt className="text-[10px] font-bold uppercase tracking-widest text-brand-dark/40">
                    Email
                  </dt>
                  <dd>{selected.email}</dd>
                </div>
                <div>
                  <dt className="text-[10px] font-bold uppercase tracking-widest text-brand-dark/40">
                    Teléfono
                  </dt>
                  <dd>{selected.telefono || "—"}</dd>
                </div>
                <div>
                  <dt className="text-[10px] font-bold uppercase tracking-widest text-brand-dark/40">
                    Empresa
                  </dt>
                  <dd>{selected.empresa || "—"}</dd>
                </div>
                <div>
                  <dt className="text-[10px] font-bold uppercase tracking-widest text-brand-dark/40">
                    Contacto
                  </dt>
                  <dd>{selected.contactoNombre || "—"}</dd>
                </div>
                {selected.motivo && (
                  <div className="sm:col-span-2">
                    <dt className="text-[10px] font-bold uppercase tracking-widest text-brand-dark/40">
                      Motivo
                    </dt>
                    <dd>{selected.motivo}</dd>
                  </div>
                )}
                {selected.servicios && (
                  <div className="sm:col-span-2">
                    <dt className="text-[10px] font-bold uppercase tracking-widest text-brand-dark/40">
                      Servicios
                    </dt>
                    <dd>{selected.servicios}</dd>
                  </div>
                )}
                <div className="sm:col-span-2">
                  <dt className="text-[10px] font-bold uppercase tracking-widest text-brand-dark/40">
                    Mensaje / comentarios
                  </dt>
                  <dd className="whitespace-pre-wrap">{selected.mensaje || "—"}</dd>
                </div>
                <div>
                  <dt className="text-[10px] font-bold uppercase tracking-widest text-brand-dark/40">
                    Vendedora
                  </dt>
                  <dd>{selected.assignedVendedorEmail || "—"}</dd>
                </div>
                <div>
                  <dt className="text-[10px] font-bold uppercase tracking-widest text-brand-dark/40">
                    Regla
                  </dt>
                  <dd>{selected.regla || "—"}</dd>
                </div>
              </dl>
              {selected.clienteId && (
                <Link
                  to={`/admin/clientes`}
                  className="text-xs font-bold text-brand-primary underline"
                >
                  Cliente ID: {selected.clienteId}
                </Link>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
    </ModuleHelpShell>
  );
}
